export const PRESENTATION_IR_VERSION = "presentation-ir.v0.1" as const;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export type EntityKind = "actor" | "prop" | "zone";

export interface PresentationEntity {
  id: string;
  label: string;
  kind: EntityKind;
  archetype: string;
  team?: "player" | "enemy" | "neutral";
  position: Vec3;
  /** Optional presentation-only Euler rotation in radians. */
  rotation?: Vec3;
  scale?: number;
  dimensions?: Vec3;
  /** Namespaced visual-kit selector. It carries no gameplay behavior. */
  presentationKit?: string;
  /** Data-only visual composition. Item IDs and color slots carry no rules. */
  appearance?: {
    items?: string[];
    colors?: {
      body?: string;
      accent?: string;
      dark?: string;
    };
  };
  anchors?: {
    hud?: Vec3;
  };
  vitals?: {
    health: number;
    maxHealth: number;
    shield?: number;
  };
}

export interface ActionPhases {
  telegraph: number;
  commit: number;
  impact: number;
  recover: number;
}

/**
 * Data-only physical intent for procedural humanoid motion. These parameters
 * describe morphology and emphasis, never whether an action succeeds.
 */
export interface ActionMotionIntent {
  hand: "left" | "right";
  delivery: "swing" | "thrust" | "bash";
  plane: "vertical" | "diagonal" | "horizontal";
  direction: -1 | 1;
  energy: number;
  reach: number;
  advance: number;
  guard: number;
  targetHeight: number;
}

/** Optional world-space presentation impulse attached to an authoritative hit. */
export interface ImpactMotionIntent {
  direction: Vec3;
  magnitude: number;
  pointHeight?: number;
}

interface TimedEvent {
  id: string;
  at: number;
}

export type PresentationEvent =
  | (TimedEvent & {
      type: "spawn";
      entityId: string;
      position?: Vec3;
    })
  | (TimedEvent & {
      type: "move";
      entityId: string;
      from: Vec3;
      to: Vec3;
      duration: number;
      gait?: "walk" | "run" | "dash";
    })
  | (TimedEvent & {
      type: "act";
      entityId: string;
      action: string;
      targetId?: string;
      phases: ActionPhases;
      tags?: string[];
      motion?: ActionMotionIntent;
    })
  | (TimedEvent & {
      type: "impact";
      sourceId: string;
      targetId: string;
      outcome: "hit" | "block" | "miss" | "heal";
      amount?: number;
      effect?: string;
      motion?: ImpactMotionIntent;
    })
  | (TimedEvent & {
      type: "status";
      entityId: string;
      status: string;
      active: boolean;
    })
  | (TimedEvent & {
      type: "despawn";
      entityId: string;
    });

export interface PresentationTrace {
  version: typeof PRESENTATION_IR_VERSION;
  id: string;
  title: string;
  source: string;
  duration: number;
  entities: PresentationEntity[];
  events: PresentationEvent[];
}

export type ActionPhase = keyof ActionPhases;

/**
 * Frozen presentation state at the instant one action hands off to another.
 * Sources provide semantic motion coordinates rather than renderer joint data,
 * so chained actions remain deterministic and rig-independent.
 */
export interface ActionTransitionSeed {
  motion: ActionMotionIntent;
  phase: ActionPhase;
  phaseProgress: number;
}

export interface ActiveAction {
  eventId: string;
  action: string;
  targetId?: string;
  phase: ActionPhase;
  phaseProgress: number;
  totalProgress: number;
  tags: string[];
  motion?: ActionMotionIntent;
  /** Optional non-idle entry pose for a chained or interrupted action. */
  transitionFrom?: ActionTransitionSeed;
}

export interface SampledEntity extends PresentationEntity {
  visible: boolean;
  position: Vec3;
  movement: {
    gait: "walk" | "run" | "dash";
    progress: number;
    /** Optional normalized locomotion weight for acceleration and stopping. */
    intensity?: number;
    /** Optional authoritative world-space velocity for directional posing. */
    velocity?: Vec3;
  } | null;
  action: ActiveAction | null;
  statuses: string[];
}

export interface ActiveImpact {
  eventId: string;
  sourceId: string;
  targetId: string;
  outcome: "hit" | "block" | "miss" | "heal";
  amount?: number;
  effect?: string;
  motion?: ImpactMotionIntent;
  age: number;
}

export interface PresentationFrame {
  time: number;
  duration: number;
  entities: SampledEntity[];
  impacts: ActiveImpact[];
  recentEvents: PresentationEvent[];
}
